import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { CalendarEvent } from '@/pages/scheduling/types';
import { supabase } from '@/integrations/supabase/client';

export const exportClientSchedulesToExcel = async (
  filteredShifts: CalendarEvent[],
  dateRange?: { from: Date | undefined; to: Date | undefined },
  teamName?: string
) => {
  try {
    // Filter shifts to exactly match the date range
    let shiftsToExport = filteredShifts;
    
    // If we have a specific day selected, include all shifts active during that day
    if (dateRange && dateRange.from && !dateRange.to) {
      // Only a single day is selected
      const selectedDate = dateRange.from;
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      const formattedSelectedDate = format(selectedDate, 'yyyy-MM-dd');
      
      shiftsToExport = filteredShifts.filter(shift => {
        const shiftStart = fromLocalISOString(shift.start_date);
        const shiftEnd = fromLocalISOString(shift.end_date);
        
        // Include shifts that:
        // 1. Start during the selected day, OR
        // 2. End during the selected day, OR
        // 3. Span across the selected day (start before and end after)
        return (
          // Shift starts during the day
          (shiftStart >= startOfDay && shiftStart <= endOfDay) ||
          // Shift ends during the day
          (shiftEnd >= startOfDay && shiftEnd <= endOfDay) ||
          // Shift spans across the day (starts before and ends after)
          (shiftStart < startOfDay && shiftEnd > endOfDay)
        );
      });
      
      console.log(`Filtered to full day (${formattedSelectedDate}): ${shiftsToExport.length} shifts`);
    }
    
    // Fetch guard information for all assigned guards in the shifts
    const guardIds = new Set<string>();
    shiftsToExport.forEach(shift => {
      if (shift.assigned_guards && shift.assigned_guards.length > 0) {
        shift.assigned_guards.forEach(guardId => guardIds.add(guardId));
      }
    });
    
    let guardsData = {};
    if (guardIds.size > 0) {
      const { data: guardProfiles, error } = await supabase
        .from('profiles')
        .select('id, code, afm')
        .in('id', Array.from(guardIds));
        
      if (error) {
        console.error("Error fetching guard profiles:", error);
      } else if (guardProfiles) {
        guardsData = guardProfiles.reduce((acc, guard) => {
          acc[guard.id] = { code: guard.code || 'No code', afm: guard.afm || 'No AFM' };
          return acc;
        }, {});
      }
    }
    
    // 1. Prepare data for export
    const exportData = shiftsToExport
      .sort((a, b) => fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime())
      .map(shift => {
        const titleParts = shift.title.match(/\[(.*?)\] (.*)/);
        const teamName = titleParts ? titleParts[1] : 'Unknown';
        const actualTitle = titleParts ? titleParts[2] : shift.title;
        
        const guardNames = shift.guard_names ? shift.guard_names.join(', ') : 'No guards assigned';
        
        // Map guard IDs to their code and AFM
        const guardCodes = [];
        const guardAfms = [];
        
        if (shift.assigned_guards && shift.assigned_guards.length > 0) {
          shift.assigned_guards.forEach(guardId => {
            if (guardsData[guardId]) {
              guardCodes.push(guardsData[guardId].code);
              guardAfms.push(guardsData[guardId].afm);
            }
          });
        }
        
        return {
          Date: format(fromLocalISOString(shift.start_date), 'dd/MM/yyyy'),
          Start: format(fromLocalISOString(shift.start_date), 'HH:mm'),
          End: format(fromLocalISOString(shift.end_date), 'HH:mm'),
          Duration: calculateDuration(shift.start_date, shift.end_date),
          Team: teamName,
          Description: actualTitle,
          Guards: guardNames,
          GuardCodes: guardCodes.join(', '),
          GuardAFMs: guardAfms.join(', ')
        };
      });
    
    // 2. Create Excel workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // 3. Set column widths
    const colWidths = [
      { wch: 12 }, // Date
      { wch: 8 },  // Start
      { wch: 8 },  // End
      { wch: 10 }, // Duration
      { wch: 15 }, // Team
      { wch: 25 }, // Description
      { wch: 30 }, // Guards
      { wch: 30 }, // GuardCodes
      { wch: 30 }  // GuardAFMs
    ];
    ws['!cols'] = colWidths;
    
    // 4. Create workbook and download
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Schedules");
    
    // 5. Generate filename with date range if provided
    let dateRangeStr = '';
    if (dateRange && dateRange.from) {
      if (dateRange.to) {
        // Date range
        dateRangeStr = `_${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to, 'yyyyMMdd')}`;
      } else {
        // Single day
        dateRangeStr = `_${format(dateRange.from, 'yyyyMMdd')}`;
      }
    }
    
    const fileNamePrefix = teamName ? `${teamName.replace(/\s+/g, '_')}_` : '';
    const fileName = `${fileNamePrefix}schedules${dateRangeStr}.xlsx`;
    
    // 6. Download file
    XLSX.writeFile(wb, fileName);
    
    return {
      success: true,
      fileName: fileName,
      recordCount: exportData.length
    };
  } catch (error) {
    console.error("Error exporting schedules:", error);
    throw error;
  }
};

const calculateDuration = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const diffMs = end.getTime() - start.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.round((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHrs === 0) {
    return `${diffMins}m`;
  } else if (diffMins === 0) {
    return `${diffHrs}h`;
  } else {
    return `${diffHrs}h ${diffMins}m`;
  }
};
